import { useMemo, useState } from 'react';
import { Upload } from 'lucide-react';
import type { FilingAttestationRequest, FilingPreviewDto } from '@signalvote/shared';
import { FINANCE_ATTESTATION_TEXT_V2, PreflightCopy } from '@signalvote/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { PendingButton } from '@/components/ui/pending-button';
import { useAuth } from '@/contexts/AuthContext';
import { US_STATE_OPTIONS } from '@/lib/usStates';
import { formatAmount, formatDate } from '../utils';

/**
 * Resolves the preview's raw jurisdiction code (`DefaultFormatStateCode ?? RulesetCode` — e.g.
 * "AL", "federal") to a treasurer-readable name for the legally-signed donor-info line. Federal
 * is not a state, so it reads "the FEC"; a state code resolves to its display name; an unknown
 * code falls back to itself (never a bare wire token like "AL" or the literal word "federal").
 */
function donorInfoJurisdictionName(stateCode: string): string {
  if (!stateCode) return 'the state';
  if (stateCode.toLowerCase() === 'federal') return 'the FEC';
  const match = US_STATE_OPTIONS.find((s) => s.value === stateCode.toUpperCase());
  return match ? match.label : stateCode;
}

interface AttestationModalProps {
  open: boolean;
  preview: FilingPreviewDto | null;
  onClose: () => void;
  onConfirm: (attestation: FilingAttestationRequest) => Promise<void>;
  isFiling: boolean;
  /** Forwarded server error from the parent — typically a typed-name
   *  mismatch the server caught (modal stays open with the message). */
  errorMessage?: string | null;
}

/**
 * FPD-002 — gates the `Ready → Filed` transition. The treasurer reviews the
 * read-only summary, ticks the required checkbox, and types their full
 * name. On Confirm the parent calls <code>POST /reports/{id}/file</code>
 * with the attestation payload; the server case-insensitively matches the
 * typed name against the user's display name before any builder work runs.
 *
 * This is NOT the legally-binding signature — that happens at the AL FCPA
 * portal upload step. This modal captures the platform-liability /
 * internal-audit "the treasurer reviewed before download" record.
 */
export function AttestationModal({
  open,
  preview,
  onClose,
  onConfirm,
  isFiling,
  errorMessage,
}: AttestationModalProps) {
  const { user } = useAuth();
  const [typedName, setTypedName] = useState('');
  const [reviewed, setReviewed] = useState(false);

  const expectedName = useMemo(
    () => composeDisplayName(user),
    [user],
  );
  // run-3 V1 — the typed-name match must never be un-completable. It is only a real,
  // deliberate name-match when the account carries a usable (non-blank) name; when it
  // doesn't (blank first/last AND blank/absent email), there is nothing to match, so we
  // fall back to a plain non-empty typed confirmation rather than a forever-disabled
  // button.
  const hasExpectedName = expectedName != null && expectedName.trim().length > 0;

  // Client-side equivalent of the server validation in
  // SubmissionPackageService.ValidateAttestationAsync — keep them in sync.
  // The server is the source of truth; we mirror the rule here only so the
  // Confirm button doesn't enable on a known-bad payload.
  const typedNameOk = hasExpectedName
    ? typedName.trim().length > 0
      && typedName.trim().toLowerCase() === expectedName!.trim().toLowerCase()
    // No usable account name → accept any deliberate non-empty typed confirmation.
    : typedName.trim().length > 0;

  const canConfirm = reviewed && typedNameOk && !isFiling;

  async function handleConfirm() {
    if (!canConfirm) return;
    await onConfirm({
      attestedByUserNameTyped: typedName.trim(),
      // FPD-005 will plumb real warning dispositions here. Until that lands
      // the modal POSTs an empty array — server accepts when there are no
      // warnings to record.
      warningDispositions: [],
      blockingExceptionType: null,
      blockingExceptionContributionId: null,
      preflightSessionId: null,
    });
  }

  function handleOpenChange(next: boolean) {
    if (!next) {
      // Reset on close so re-opening starts fresh.
      setTypedName('');
      setReviewed(false);
      onClose();
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {/* §C — bound the modal to the viewport and scroll the body so the
          "Confirm and download" button is never pushed below the fold (large
          summaries + zoom/font can otherwise overflow the window). */}
      <DialogContent className="flex max-h-[90vh] flex-col gap-0">
        <div data-testid="attestation-modal" />
        <DialogHeader>
          <DialogTitle>Confirm filing</DialogTitle>
          <DialogDescription>
            Review the figures and confirm. The file downloads after confirmation;
            uploading and signing at the state portal is the legally-binding step.
          </DialogDescription>
        </DialogHeader>

        {/* Scrollable body between the header and the pinned footer. */}
        <div
          data-testid="attestation-body"
          className="mt-4 min-h-0 flex-1 space-y-4 overflow-y-auto pr-1"
        >
        {preview && (
          <div
            className="space-y-2 rounded-md border bg-muted/30 p-3 text-sm"
            data-testid="attestation-summary"
          >
            <div>
              <span className="font-medium">Period:</span>{' '}
              {formatDate(preview.periodStart)} – {formatDate(preview.periodEnd)}
            </div>
            {/* Issue #146 — the numbers the treasurer legally attests to read the
                will-file projection (what actually uploads), not the raw
                Confirmed-in-period `itemized` block, so the attestation is truthful
                on amendments / processor-status drift / filed reports. Expenditures
                already reflect the filed-expenditure set. */}
            <div>
              <span className="font-medium">Contributions:</span>{' '}
              {preview.willFile.count} ({formatAmount(preview.willFile.total)})
            </div>
            <div>
              <span className="font-medium">Expenditures:</span>{' '}
              {preview.expenditures.count} ({formatAmount(preview.expenditures.total)})
            </div>
          </div>
        )}

        {/* FLW-003 — dynamic context copy rendered ABOVE the frozen statement.
            The donor-info-requested N is informational; the per-row attestation
            rows are the forensic record — never "fix" this by embedding N in the
            V2 statement (that would make every distinct N a new statement
            version). */}
        {preview && (preview.treasurerName || preview.donorInfoRequestedCount > 0) && (
          <div className="space-y-1 text-sm text-muted-foreground" data-testid="attestation-context">
            {preview.treasurerName && (
              <p data-testid="attestation-treasurer-line">
                {PreflightCopy.AttestationTreasurerContextLine.replace(
                  '{treasurerName}',
                  preview.treasurerName,
                )}
              </p>
            )}
            {preview.donorInfoRequestedCount > 0 && (
              <p data-testid="attestation-donor-info-line">
                {PreflightCopy.AttestationDonorInfoRequestedContextLine
                  .replace('{state}', donorInfoJurisdictionName(preview.stateCode))
                  .replace('{N}', String(preview.donorInfoRequestedCount))}
              </p>
            )}
          </div>
        )}

        <div
          className="rounded-md border bg-background p-3 text-sm"
          data-testid="attestation-statement"
        >
          {FINANCE_ATTESTATION_TEXT_V2}
        </div>

        <label
          className="flex items-start gap-2 text-sm"
          data-testid="attestation-checkbox-label"
        >
          <input
            type="checkbox"
            className="mt-1"
            checked={reviewed}
            onChange={(e) => setReviewed(e.target.checked)}
            data-testid="attestation-reviewed-checkbox"
          />
          <span>I have reviewed this filing and attest to its accuracy.</span>
        </label>

        <div className="space-y-1">
          <label
            htmlFor="attestation-typed-name"
            className="text-sm font-medium"
          >
            Type your full name to confirm
          </label>
          <input
            id="attestation-typed-name"
            type="text"
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
            placeholder={hasExpectedName ? expectedName! : 'Your full name'}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            data-testid="attestation-typed-name-input"
            autoComplete="off"
          />
          {/* The account-name-match hint only applies when there IS a name to match; with
              no usable account name the field just needs a non-empty confirmation. */}
          {hasExpectedName && !typedNameOk && typedName.length > 0 && (
            <p
              className="text-xs text-destructive"
              data-testid="attestation-name-hint"
            >
              Must match your account name (case-insensitive).
            </p>
          )}
        </div>

        {errorMessage && (
          <div
            className="rounded-md border border-destructive bg-destructive/10 p-2 text-sm text-destructive"
            data-testid="attestation-error"
            role="alert"
          >
            {errorMessage}
          </div>
        )}
        </div>

        <DialogFooter className="mt-4 flex gap-2">
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isFiling}>
            Cancel
          </Button>
          <PendingButton
            pending={isFiling}
            icon={<Upload className="h-4 w-4" />}
            onClick={handleConfirm}
            disabled={!canConfirm}
            data-testid="attestation-confirm-button"
          >
            {isFiling ? 'Filing…' : 'Confirm and download'}
          </PendingButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function composeDisplayName(user: { firstName?: string | null; lastName?: string | null; email?: string | null } | null): string | null {
  if (!user) return null;
  const first = (user.firstName ?? '').trim();
  const last = (user.lastName ?? '').trim();
  if (first.length === 0 && last.length === 0) {
    // Server falls back to UserName when name parts are blank; on the
    // client we don't have the user's username directly, so render the
    // email as a best-effort placeholder. The server is the source of
    // truth — if the typed value doesn't match, the modal stays open with
    // the server-returned message.
    return user.email ?? null;
  }
  return (first + ' ' + last).trim();
}
